import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import {
  Upload,
  Trash2,
  Copy,
  Check,
  Image as ImageIcon,
  FileText,
  Loader2,
  FolderOpen,
} from "lucide-react";

const API_BASE =
  import.meta.env.VITE_API_URL || "https://ckgo2-api.martin-94d.workers.dev";

interface MediaFile {
  key: string;
  url: string;
  size: number;
  uploaded: string;
  contentType: string;
}

interface ListResponse {
  files: MediaFile[];
  cursor?: string;
  hasMore: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("cs-CZ", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isImage(contentType: string): boolean {
  return contentType.startsWith("image/");
}

export default function MediaPage() {
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  // Fetch files
  const { data, isLoading, error } = useQuery({
    queryKey: ["media-files"],
    queryFn: () => api.get<ApiResponse<ListResponse>>("/api/admin/upload"),
  });

  const files = data?.data?.files || [];

  // Upload mutation
  const uploadMutation = useMutation({
    mutationFn: async (files: FileList) => {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }
      formData.append("folder", "uploads");

      const response = await fetch(`${API_BASE}/api/admin/upload/multiple`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media-files"] });
      setUploadError(null);
    },
    onError: (error) => {
      setUploadError(error instanceof Error ? error.message : "Upload failed");
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (keys: string[]) => {
      if (keys.length === 1) {
        return api.delete("/api/admin/upload", { key: keys[0] });
      }
      return api.delete("/api/admin/upload/multiple", { keys });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media-files"] });
      setSelectedFiles(new Set());
    },
  });

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      uploadMutation.mutate(e.target.files);
    }
  };

  // Handle drag & drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        uploadMutation.mutate(e.dataTransfer.files);
      }
    },
    [uploadMutation]
  );

  // Copy URL to clipboard
  const copyUrl = async (url: string, key: string) => {
    await navigator.clipboard.writeText(url);
    setCopiedKey(key);
    setTimeout(() => setCopiedKey(null), 2000);
  };

  // Toggle file selection
  const toggleSelection = (key: string) => {
    setSelectedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Delete selected files
  const deleteSelected = () => {
    if (selectedFiles.size > 0) {
      if (
        confirm(`Opravdu chcete smazat ${selectedFiles.size} souborů?`)
      ) {
        deleteMutation.mutate(Array.from(selectedFiles));
      }
    }
  };

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání souborů. Zkuste to prosím znovu.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Média
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Správa obrázků a souborů
          </p>
        </div>
        <div className="flex gap-2">
          {selectedFiles.size > 0 && (
            <button
              onClick={deleteSelected}
              disabled={deleteMutation.isPending}
              className="btn-ghost text-error-600 hover:bg-error-50"
            >
              <Trash2 className="h-5 w-5" />
              Smazat ({selectedFiles.size})
            </button>
          )}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="btn-primary"
          >
            <Upload className="h-5 w-5" />
            Nahrát soubory
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
        </div>
      </div>

      {/* Upload error */}
      {uploadError && (
        <div className="rounded-lg bg-error-50 p-4 text-error-700 dark:bg-error-900/20 dark:text-error-400">
          {uploadError}
        </div>
      )}

      {/* Drop zone / File grid */}
      <div
        className={`card min-h-[400px] p-6 transition-colors ${
          isDragging
            ? "border-2 border-dashed border-primary-500 bg-primary-50 dark:bg-primary-900/10"
            : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {uploadMutation.isPending && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-primary-50 p-4 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400">
            <Loader2 className="h-5 w-5 animate-spin" />
            Nahrávám soubory...
          </div>
        )}

        {isLoading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
          </div>
        ) : files.length === 0 ? (
          <div className="flex h-64 flex-col items-center justify-center text-gray-500">
            <FolderOpen className="mb-4 h-16 w-16 text-gray-300" />
            <p className="text-lg font-medium">Žádné soubory</p>
            <p className="mt-1 text-sm">
              Přetáhněte soubory sem nebo klikněte na "Nahrát soubory"
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
            {files.map((file) => (
              <div
                key={file.key}
                className={`group relative rounded-lg border-2 transition-all ${
                  selectedFiles.has(file.key)
                    ? "border-primary-500 bg-primary-50 dark:bg-primary-900/20"
                    : "border-transparent hover:border-gray-200 dark:hover:border-gray-700"
                }`}
              >
                {/* Selection checkbox */}
                <button
                  onClick={() => toggleSelection(file.key)}
                  className={`absolute left-2 top-2 z-10 flex h-6 w-6 items-center justify-center rounded border-2 transition-all ${
                    selectedFiles.has(file.key)
                      ? "border-primary-500 bg-primary-500 text-white"
                      : "border-gray-300 bg-white opacity-0 group-hover:opacity-100 dark:border-gray-600 dark:bg-gray-800"
                  }`}
                >
                  {selectedFiles.has(file.key) && (
                    <Check className="h-4 w-4" />
                  )}
                </button>

                {/* Image/File preview */}
                <div className="aspect-square overflow-hidden rounded-t-lg bg-gray-100 dark:bg-gray-800">
                  {isImage(file.contentType) || file.key.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ? (
                    <img
                      src={file.url}
                      alt={file.key}
                      className="h-full w-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        console.error("Image load error:", file.url);
                        (e.target as HTMLImageElement).style.display = "none";
                        (e.target as HTMLImageElement).parentElement!.innerHTML = `<div class="flex h-full items-center justify-center flex-col gap-2"><svg class="h-12 w-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path></svg><span class="text-xs text-red-500">Chyba</span></div>`;
                      }}
                    />
                  ) : (
                    <div className="flex h-full items-center justify-center">
                      <FileText className="h-12 w-12 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* File info */}
                <div className="p-2">
                  <p
                    className="truncate text-xs font-medium text-gray-900 dark:text-white"
                    title={file.key}
                  >
                    {file.key.split("/").pop()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>

                {/* Actions */}
                <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                  <button
                    onClick={() => copyUrl(file.url, file.key)}
                    className="flex h-7 w-7 items-center justify-center rounded bg-white/90 text-gray-700 shadow hover:bg-white dark:bg-gray-800/90 dark:text-gray-300 dark:hover:bg-gray-800"
                    title="Kopírovat URL"
                  >
                    {copiedKey === file.key ? (
                      <Check className="h-4 w-4 text-green-500" />
                    ) : (
                      <Copy className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={() => {
                      if (confirm("Opravdu chcete smazat tento soubor?")) {
                        deleteMutation.mutate([file.key]);
                      }
                    }}
                    className="flex h-7 w-7 items-center justify-center rounded bg-white/90 text-error-600 shadow hover:bg-white dark:bg-gray-800/90 dark:hover:bg-gray-800"
                    title="Smazat"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Drag overlay */}
        {isDragging && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-lg bg-primary-500/10">
            <div className="text-center">
              <Upload className="mx-auto h-12 w-12 text-primary-500" />
              <p className="mt-2 font-medium text-primary-600">
                Pusťte soubory pro nahrání
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>
          {files.length} souborů{" "}
          {selectedFiles.size > 0 && `(${selectedFiles.size} vybráno)`}
        </span>
        <span>
          Celkem:{" "}
          {formatFileSize(files.reduce((acc, f) => acc + f.size, 0))}
        </span>
      </div>
    </div>
  );
}
